// The "look before it sends" popup. Mounted once for signed-in users (App.js). It draws nothing
// until a button hands an email to utils/emailReviewGate.js; then it shows every email that button
// is about to send, one tab each, so the user can read it, tweak the subject or wording for this
// one send, leave one out, send them all, or cancel. The gate does the waiting and batching; this
// file is only the screen.
import React, { useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { StateContext } from "../../context/StateProvider";
import { readBccMe, writeBccMe } from "../../utils/bccMe";
import {
  registerEmailReviewHost,
  subscribeEmailReview,
  getEmailReviewSnapshot,
  resolveEmailReview,
  cancelEmailReview,
  emailHtmlToText,
  replaceBodyInner,
} from "../../utils/emailReviewGate";
import { recipientLine } from "../../utils/emailRecipientLine";

// The signed-in person, read without useStateValue so the popup never throws outside the provider
// (a test, or a page that mounts it on its own).
function useSignedInUser() {
  const ctx = useContext(StateContext);
  return (Array.isArray(ctx) && ctx[0] && ctx[0].user) || null;
}

const MIN_FRAME_HEIGHT = 360;
const DEFAULT_LOCKED_NOTE = "Edit the wording on the previous screen.";

const tabLabel = (draft) => draft.label || draft.toName || draft.to || "Email";
// Several addresses (every client on a file) read as "Sara and Charlie: a@x.com, b@y.com", never as
// one broken address in angle brackets (utils/emailRecipientLine.js).
const draftRecipientLine = (draft) => recipientLine(draft.toName, draft.to);

/**
 * What the user left in an editable email frame, or null when nothing changed. Compares the body
 * with how it looked right after it loaded, so an untouched email goes out byte for byte as built,
 * and only the body is swapped back in so the email's <head> styles survive.
 */
export function readEditedFrameHtml(originalHtml, loadedBodyHtml, frameDoc) {
  const body = frameDoc && frameDoc.body;
  if (!body || loadedBodyHtml == null) return null;
  const now = body.innerHTML;
  if (now === loadedBodyHtml) return null;
  return replaceBodyInner(originalHtml, now);
}

// Grow an email frame to its content (never below MIN_FRAME_HEIGHT). The modal body scrolls.
function fitFrame(el) {
  const doc = el?.contentDocument;
  if (!el || !doc || !doc.documentElement) return;
  const h = Math.max(MIN_FRAME_HEIGHT, doc.documentElement.scrollHeight || 0);
  el.style.height = `${h}px`;
}

export default function EmailReviewHost() {
  useEffect(() => registerEmailReviewHost(), []);
  const batch = useSyncExternalStore(subscribeEmailReview, getEmailReviewSnapshot, getEmailReviewSnapshot);
  if (!batch) return null;
  return <EmailReviewModal key={batch.id} batch={batch} />;
}

function EmailReviewModal({ batch }) {
  const { items } = batch;
  const [activeId, setActiveId] = useState(items[0]?.id);
  // id -> { subject?, text?, skip? }; anything missing falls back to the draft as built.
  const [edits, setEdits] = useState({});
  // id -> the iframe element and its body html right after loading (for the edited check).
  const frames = useRef({});

  const active = items.find((i) => i.id === activeId) || items[0];
  const editFor = (id) => edits[id] || {};
  const setEdit = (id, patch) => setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const multiple = items.length > 1;
  const sendCount = items.filter((i) => !editFor(i.id).skip).length;
  const note = items.map((i) => i.draft.reviewNote).find(Boolean);

  const cancel = useCallback(() => cancelEmailReview(), []);

  // BCC Me (Henry, 09/28/2026): a blind copy to the person clicking Send, for every email in this
  // popup. Only their own address; clients stay on To. Remembered per person in this browser.
  const me = useSignedInUser();
  const myEmail = String(me?.email || "").trim();
  const [bccMe, setBccMe] = useState(() => readBccMe(me?.id));
  const toggleBccMe = (on) => {
    setBccMe(on);
    writeBccMe(me?.id, on);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cancel]);

  const send = () => {
    const out = {};
    items.forEach(({ id, draft }) => {
      const e = editFor(id);
      if (e.skip) {
        out[id] = null;
        return;
      }
      const result = {};
      if (e.subject !== undefined) result.subject = e.subject;
      if (draft.lockBody) {
        // The wording was set on the screen before; only the subject may change.
      } else if (draft.html) {
        const f = frames.current[id];
        let edited = null;
        try {
          edited = readEditedFrameHtml(draft.html, f?.loadedBody, f?.el?.contentDocument);
        } catch {
          edited = null;
        }
        if (edited != null) {
          result.html = edited;
          result.text = emailHtmlToText(edited);
        }
      } else if (e.text !== undefined) {
        result.text = e.text;
      }
      if (bccMe && myEmail) result.bccSelf = myEmail;
      out[id] = result;
    });
    resolveEmailReview(out);
  };

  const onFrameLoad = (id, locked) => {
    const el = frames.current[id]?.el;
    const doc = el?.contentDocument;
    if (!doc || !doc.body) return;
    if (!locked) {
      try {
        doc.designMode = "on";
      } catch {
        /* read-only preview is still fine */
      }
    }
    frames.current[id] = { el, loadedBody: doc.body.innerHTML };
    fitFrame(el);
    doc.addEventListener("input", () => fitFrame(el));
    doc.addEventListener("keydown", (e) => {
      if (e.key === "Escape") cancel();
    });
  };

  // A frame measured while its tab was hidden has no height yet; measure again when it shows.
  const activeKey = active?.id;
  useEffect(() => {
    if (activeKey) fitFrame(frames.current[activeKey]?.el);
  }, [activeKey]);

  const title = multiple ? `Review ${items.length} Emails Before Sending` : "Review This Email Before Sending";
  const sendLabel = sendCount > 1 ? `Send All ${sendCount}` : "Send";

  return (
    <div className="fixed inset-0 z-[10000] flex items-stretch justify-center bg-slate-900/55 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-review-title"
        className="flex h-full w-full flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[90vh] sm:max-w-3xl sm:rounded-2xl sm:border sm:border-gray-200 sm:shadow-sm"
      >
        <div className="flex items-center justify-between bg-[#1E293B] px-5 py-3">
          <h2 id="email-review-title" className="text-base font-semibold text-white">
            {title}
          </h2>
          <button
            type="button"
            onClick={cancel}
            aria-label="Close"
            className="rounded-md px-2 text-xl leading-none text-white/80 hover:text-white"
          >
            ×
          </button>
        </div>

        {multiple && (
          <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-gray-200 px-3 pt-2">
            {items.map((item) => {
              const selected = item.id === active.id;
              const skipped = !!editFor(item.id).skip;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActiveId(item.id)}
                  className={`whitespace-nowrap rounded-t-lg border-b-2 px-3 py-2 text-sm ${
                    selected ? "border-[#1E293B] font-semibold text-[#1E293B]" : "border-transparent text-gray-500 hover:text-gray-800"
                  } ${skipped ? "line-through opacity-60" : ""}`}
                >
                  {tabLabel(item.draft)}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.map((item) => {
            const { id, draft } = item;
            const e = editFor(id);
            const hidden = id !== active.id;
            const locked = !!draft.lockBody;
            const lockedNote = draft.lockBodyNote || DEFAULT_LOCKED_NOTE;
            return (
              // Only the tab on show draws its controls. Every email's frame stays mounted, hidden,
              // so wording changed in one tab is still there after looking at another.
              <div key={id} hidden={hidden} className="space-y-3">
                {!hidden && multiple && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!e.skip}
                      onChange={(ev) => setEdit(id, { skip: ev.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Don't Send This One
                  </label>
                )}

                {!hidden && (
                  <dl className="space-y-1 text-sm text-gray-700">
                    <div className="flex gap-2">
                      <dt className="w-10 shrink-0 font-medium text-gray-500">To</dt>
                      <dd className="break-all">{draftRecipientLine(draft)}</dd>
                    </div>
                    {draft.cc ? (
                      <div className="flex gap-2">
                        <dt className="w-10 shrink-0 font-medium text-gray-500">Cc</dt>
                        <dd className="break-all">{draft.cc}</dd>
                      </div>
                    ) : null}
                    {draft.bcc ? (
                      <div className="flex gap-2">
                        <dt className="w-10 shrink-0 font-medium text-gray-500">Bcc</dt>
                        <dd className="break-all">{draft.bcc}</dd>
                      </div>
                    ) : null}
                  </dl>
                )}

                {!hidden && (
                  <div>
                    <label htmlFor={`email-review-subject-${id}`} className="mb-1 block text-sm font-medium text-gray-700">
                      Subject
                    </label>
                    <input
                      id={`email-review-subject-${id}`}
                      type="text"
                      value={e.subject !== undefined ? e.subject : draft.subject || ""}
                      onChange={(ev) => setEdit(id, { subject: ev.target.value })}
                      disabled={!!e.skip}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#218FDA] focus:outline-none focus:ring-1 focus:ring-[#218FDA] disabled:bg-gray-50"
                    />
                  </div>
                )}

                {draft.html ? (
                  <div>
                    {!hidden && (
                      <p className="mb-1 text-sm font-medium text-gray-700">
                        Message{" "}
                        <span className="font-normal text-gray-500">
                          {locked ? lockedNote : "(click into the email to change the wording)"}
                        </span>
                      </p>
                    )}
                    <iframe
                      title={`Email preview: ${tabLabel(draft)}`}
                      srcDoc={draft.html}
                      sandbox="allow-same-origin"
                      ref={(el) => {
                        if (el) frames.current[id] = { ...frames.current[id], el };
                      }}
                      onLoad={() => onFrameLoad(id, locked)}
                      style={{ minHeight: MIN_FRAME_HEIGHT }}
                      className={`block w-full rounded-lg border border-gray-200 bg-white ${e.skip ? "pointer-events-none opacity-50" : ""}`}
                    />
                  </div>
                ) : (
                  !hidden && (
                    <div>
                      <label htmlFor={`email-review-text-${id}`} className="mb-1 block text-sm font-medium text-gray-700">
                        Message
                      </label>
                      <textarea
                        id={`email-review-text-${id}`}
                        value={!locked && e.text !== undefined ? e.text : draft.text || ""}
                        onChange={(ev) => !locked && setEdit(id, { text: ev.target.value })}
                        readOnly={locked}
                        disabled={!!e.skip}
                        rows={12}
                        className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#218FDA] focus:outline-none focus:ring-1 focus:ring-[#218FDA] disabled:bg-gray-50 ${locked ? "bg-gray-50 text-gray-600" : ""}`}
                      />
                      {locked ? <p className="mt-1 text-xs text-gray-500">{lockedNote}</p> : null}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-gray-200 px-5 py-3">
          {note ? <p className="mb-1 text-sm text-gray-700">{note}</p> : null}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1">
              {myEmail ? (
                <label className="flex items-center gap-2 text-sm text-gray-700" title={`A blind copy goes to ${myEmail}`}>
                  <input
                    type="checkbox"
                    checked={bccMe}
                    onChange={(ev) => toggleBccMe(ev.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  BCC Me
                </label>
              ) : null}
              <p className="text-xs text-gray-500">Edits apply to this send only.</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={cancel}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:flex-none"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={send}
                disabled={sendCount === 0}
                className="flex-1 rounded-lg bg-[#1E293B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0f172a] disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
              >
                {sendLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
